import type {
  WearableConnectSession,
  WearableConnection,
} from "@/types/wearable";

import { requestData } from "@/api/api-config";

export async function getPatientWearableConnections(
  patientId: string,
): Promise<WearableConnection[] | null> {
  return await requestData<WearableConnection[]>(
    `user/patient/wearable/${patientId}`,
    "get",
  );
}

/**
 * A link to send the patient, for them to open on their own phone.
 *
 * The only way to start a connection from the chart. The landing page a session
 * redirects to is the backend's decision, never a parameter — a route that took
 * one from the caller would be an open redirect.
 */
export async function createPatientWearableSharedConnectSession(
  patientId: string,
): Promise<WearableConnectSession | null> {
  return await requestData<WearableConnectSession>(
    `user/patient/wearable/${patientId}/shared-connect-session`,
    "post",
  );
}

/**
 * `catchError: false` so a failure REJECTS instead of resolving to null.
 *
 * The default swallows an HTTPError into `console.error(); return null`, which
 * a caller cannot tell from a successful disconnect — the mutation resolves,
 * its `catch` never runs, and the clinician gets a green "Device disconnected"
 * toast while the device keeps streaming. That is the one outcome this call
 * must never produce, so it is the one call that opts out.
 */
export async function disconnectPatientWearable(
  patientId: string,
  connectionId: string,
): Promise<WearableConnection | null> {
  return await requestData<WearableConnection>(
    `user/patient/wearable/${patientId}/connection/${connectionId}`,
    "delete",
    undefined,
    false,
  );
}

/**
 * Reads straight out of the aggregator's export bucket instead of waiting for
 * the daily sweep. A longer timeout than the default 20s: a bucket read of
 * many objects can genuinely take longer than an ordinary request.
 */
export async function syncPatientWearableExports(
  patientId: string,
): Promise<WearableConnection[] | null> {
  return await requestData<WearableConnection[]>(
    `user/patient/wearable/${patientId}/sync`,
    "post",
    undefined,
    // Rejects rather than resolving to null, for the reason on `disconnectPatientWearable`
    // above: "Readings imported" is worse than no toast when nothing was imported,
    // and a 120s timeout is exactly when that would fire.
    false,
    120_000,
  );
}
